/**
 * token.ts - Token 管理逻辑
 *
 * 功能说明：
 * - Token 获取、存储、刷新
 * - API 基础 URL 配置
 *
 * 依赖关系：
 * - 被 store/authStore.ts 导入使用
 *
 * 关键注释索引：
 * - [API配置] - 见下方 API_BASE_URL
 * - [Token刷新] - 见下方 refreshToken 函数
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

/**
 * [API配置] API 基础 URL
 *
 * @description 与 api.ts 保持一致的配置逻辑
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * [浏览器检测] 检测是否在浏览器环境
 */
const isBrowser = (): boolean => typeof window !== 'undefined' && typeof navigator !== 'undefined';

/**
 * [获取Token] 从 localStorage 获取 Token
 *
 * @description 兼容 Zustand persist 的存储格式
 */
export const getTokenFromStorage = (): string | null => {
  if (!isBrowser()) return null;

  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (!authStorage) return null;

    const authState = JSON.parse(authStorage);
    return authState?.state?.token || authState?.token || null;
  } catch {
    return null;
  }
};

/**
 * [Token刷新] 刷新认证 Token
 *
 * @description 向后端请求新的 Token
 *
 * @param getState - 获取当前状态的函数
 * @param setState - 设置状态的函数
 * @returns 是否刷新成功
 */
export const refreshToken = async (
  getState: () => { token: string | null },
  setState: (state: Partial<{ isAuthenticated: boolean; user: null; token: string | null }>) => void
): Promise<boolean> => {
  const { token } = getState();

  if (!token) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken: token }),
    });

    const data = await response.json();

    if (data.success) {
      setState({ token: data.token });
      return true;
    } else {
      // 刷新失败，清除认证状态
      setState({
        isAuthenticated: false,
        user: null,
        token: null,
      });
      return false;
    }
  } catch (error) {
    console.error('刷新令牌失败:', error);
    setState({
      isAuthenticated: false,
      user: null,
      token: null,
    });
    return false;
  }
};

/**
 * [注销请求] 发送注销请求到后端
 *
 * @description 异步发送，不阻塞本地状态清除
 */
export const sendLogoutRequest = async (
  user: { id: string; username: string } | null,
  token: string | null
): Promise<void> => {
  if (!user || !token) return;

  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        userId: user.id,
        username: user.username,
      }),
    });
  } catch (error) {
    console.error('注销请求失败:', error);
  }
};
